import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  type User,
  onAuthStateChanged,
  signInWithCustomToken,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, setDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { auth, db, functions } from '../lib/firebase';
import { buildAuthUrl, exchangeCodeForTokens, getAuthentikConfig } from '../lib/authentik';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  signInWithAuthentik: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  registerWithEmail: (email: string, password: string, displayName?: string) => Promise<void>;
  signInWithDemo: () => Promise<void>;
  signOut: () => Promise<void>;
  isAuthentikConfigured: boolean;
  isDemo: boolean;
  isPro: boolean;
  proExpiresAt: string | null;
  proLoading: boolean;
  isBasic: boolean;
  basicExpiresAt: string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDemo, setIsDemo] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [proExpiresAt, setProExpiresAt] = useState<string | null>(null);
  const [proLoading, setProLoading] = useState(true);
  const [isBasic, setIsBasic] = useState(false);
  const [basicExpiresAt, setBasicExpiresAt] = useState<string | null>(null);

  // Handle redirect result (fallback from popup-blocked)
  useEffect(() => {
    getRedirectResult(auth).then(async (result) => {
      if (!result?.user) return;
      setIsDemo(false);
      const userRef = doc(db, 'users', result.user.uid);
      await setDoc(userRef, {
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL,
        provider: 'google',
        lastLogin: serverTimestamp(),
        createdAt: serverTimestamp(),
      }, { merge: true });
    }).catch((err) => {
      console.error('Redirect result error:', err);
    });
  }, []);

  // Check if Authentik is configured
  const isAuthentikConfigured = !!(
    import.meta.env.VITE_AUTHENTIK_CLIENT_ID &&
    import.meta.env.VITE_AUTHENTIK_BASE_URL
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!isDemo) {
        setUser(firebaseUser);
      }
      setLoading(false);

      // Update last login timestamp
      if (firebaseUser && !isDemo) {
        const userRef = doc(db, 'users', firebaseUser.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          await setDoc(userRef, { lastLogin: serverTimestamp() }, { merge: true });
        }
      }
    });

    return () => unsubscribe();
  }, [isDemo]);

  // Listen for PRO status changes
  useEffect(() => {
    if (!user || isDemo) {
      setIsPro(false);
      setProExpiresAt(null);
      setProLoading(false);
      setIsBasic(false);
      setBasicExpiresAt(null);
      return;
    }

    setProLoading(true);
    const userRef = doc(db, 'users', user.uid);

    const unsubscribe = onSnapshot(userRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const userIsPro = data.isPro === true;
        const proExpiry = data.proExpiresAt?.toDate?.()?.toISOString() || null;

        if (userIsPro && proExpiry && new Date(proExpiry) < new Date()) {
          setIsPro(false);
          setProExpiresAt(null);
        } else {
          setIsPro(userIsPro);
          setProExpiresAt(proExpiry);
        }

        const userIsBasic = data.isBasic === true || data.planId === 'basic';
        const basicExpiry = data.basicExpiresAt?.toDate?.()?.toISOString() || null;

        if (userIsBasic && basicExpiry && new Date(basicExpiry) < new Date()) {
          setIsBasic(false);
          setBasicExpiresAt(null);
        } else {
          setIsBasic(userIsBasic);
          setBasicExpiresAt(basicExpiry);
        }
      } else {
        setIsPro(false);
        setProExpiresAt(null);
        setIsBasic(false);
        setBasicExpiresAt(null);
      }
      setProLoading(false);
    }, (error) => {
      console.error('Error listening to plan status:', error);
      setProLoading(false);
    });

    return () => unsubscribe();
  }, [user, isDemo]);

  // Handle Authentik callback
  useEffect(() => {
    const handleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const storedState = sessionStorage.getItem('oauth_state');

      if (code && state && state === storedState) {
        try {
          setLoading(true);
          setError(null);

          // Exchange code for tokens
          const tokens = await exchangeCodeForTokens(code);
          console.log('Tokens received:', Object.keys(tokens));
          console.log('Has id_token:', !!tokens.id_token);
          console.log('Has access_token:', !!tokens.access_token);

          if (!tokens.id_token) {
            throw new Error('No id_token received from Authentik. Make sure OpenID scope is enabled.');
          }

          // Call Firebase function to create custom token
          const authentikAuth = httpsCallable<
            { idToken: string; authentikIssuer: string },
            { customToken: string; uid: string }
          >(functions, 'authentikAuth');

          const config = getAuthentikConfig();
          const result = await authentikAuth({
            idToken: tokens.id_token,
            authentikIssuer: config.issuer,
          });

          // Sign in with custom token
          await signInWithCustomToken(auth, result.data.customToken);

          // Clean URL
          window.history.replaceState({}, document.title, '/');
        } catch (err) {
          console.error('Authentik callback error:', err);
          setError(err instanceof Error ? err.message : 'Authentication failed');
        } finally {
          setLoading(false);
        }
      }
    };

    if (window.location.pathname === '/auth/callback') {
      handleCallback();
    }
  }, []);

  const signInWithAuthentik = async () => {
    try {
      setError(null);
      const { url } = await buildAuthUrl();
      window.location.href = url;
    } catch (err) {
      console.error('Authentik login error:', err);
      setError(err instanceof Error ? err.message : 'Failed to start authentication');
    }
  };

  const signInWithGoogle = async () => {
    setError(null);
    setLoading(true);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    try {
      const result = await signInWithPopup(auth, provider);
      setIsDemo(false);
      const userRef = doc(db, 'users', result.user.uid);
      await setDoc(userRef, {
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL,
        provider: 'google',
        lastLogin: serverTimestamp(),
        createdAt: serverTimestamp(),
      }, { merge: true });
    } catch (err: any) {
      if (err?.code === 'auth/popup-blocked' || err?.code === 'auth/popup-closed-by-user') {
        // Popup blocked — fall back to redirect (result handled on next load via getRedirectResult)
        await signInWithRedirect(auth, provider);
        return;
      }
      console.error('Google login error:', err);
      setError(err instanceof Error ? err.message : 'Google authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const mapAuthError = (err: any): string => {
    const code = err?.code as string | undefined;
    const map: Record<string, string> = {
      'auth/user-not-found': 'Nie znaleziono konta z tym adresem email.',
      'auth/wrong-password': 'Nieprawidłowe hasło.',
      'auth/invalid-credential': 'Nieprawidłowy email lub hasło.',
      'auth/invalid-email': 'Nieprawidłowy adres email.',
      'auth/email-already-in-use': 'Ten adres email jest już zajęty.',
      'auth/weak-password': 'Hasło jest za słabe (minimum 6 znaków).',
      'auth/too-many-requests': 'Zbyt wiele prób. Spróbuj ponownie później.',
    };
    return (code && map[code]) || (err instanceof Error ? err.message : 'Błąd uwierzytelniania.');
  };

  const signInWithEmail = async (email: string, password: string) => {
    setError(null);
    setLoading(true);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      setIsDemo(false);
      const userRef = doc(db, 'users', result.user.uid);
      await setDoc(userRef, {
        email: result.user.email,
        displayName: result.user.displayName,
        provider: 'email',
        lastLogin: serverTimestamp(),
      }, { merge: true });
    } catch (err: any) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const registerWithEmail = async (email: string, password: string, displayName?: string) => {
    setError(null);
    setLoading(true);
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      if (displayName) {
        await updateProfile(result.user, { displayName });
      }
      setIsDemo(false);
      const userRef = doc(db, 'users', result.user.uid);
      await setDoc(userRef, {
        email: result.user.email,
        displayName: displayName || result.user.displayName || '',
        provider: 'email',
        lastLogin: serverTimestamp(),
        createdAt: serverTimestamp(),
      }, { merge: true });
    } catch (err: any) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const signInWithDemo = async () => {
    try {
      setLoading(true);
      setError(null);
      setIsDemo(true);
      // Mock user object
      const demoUser = {
        uid: 'demo-user-123',
        email: 'demo@example.com',
        displayName: 'Demo User',
        photoURL: null,
        emailVerified: true,
        isAnonymous: false,
        metadata: {},
        providerData: [],
        refreshToken: '',
        tenantId: null,
        delete: async () => {},
        getIdToken: async () => 'demo-token',
        getIdTokenResult: async () => ({
            token: 'demo-token',
            signInProvider: 'custom',
            claims: {},
            authTime: Date.now().toString(),
            issuedAtTime: Date.now().toString(),
            expirationTime: (Date.now() + 3600000).toString(),
        }),
        reload: async () => {},
        toJSON: () => ({}),
        phoneNumber: null,
        providerId: 'custom'
      } as unknown as User;
      
      setUser(demoUser);
    } catch(err) {
       console.error("Demo login failed", err);
       setError("Failed to enter demo mode");
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      if (isDemo) {
        setIsDemo(false);
        setUser(null);
        setIsPro(false);
        setProExpiresAt(null);
      } else {
        await firebaseSignOut(auth);
      }
    } catch (err) {
      console.error('Sign out error:', err);
      setError(err instanceof Error ? err.message : 'Sign out failed');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        signInWithAuthentik,
        signInWithGoogle,
        signInWithEmail,
        registerWithEmail,
        signInWithDemo,
        signOut,
        isAuthentikConfigured,
        isDemo,
        isPro,
        proExpiresAt,
        proLoading,
        isBasic,
        basicExpiresAt,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
